<template>
  <div>
    <FilterBar class="w-full flex-1">
      <GenericMultiFilter
        :options="companies"
        v-model="selectedCompanies"
        title="Companies"
        chip-title-field="label"
        data-test="company-filter"
        data-test-prefix="company-filter" />

      <GenericMultiFilter
        :options="coverageTypeList"
        v-model="selectedCoverageTypes"
        title="Coverage Types"
        chip-title-field="label"
        data-test="coverageTypes-filter"
        data-test-prefix="coverageTypes-filter" />

      <div class="flex items-center">
        <a
          v-show="filterApplied"
          @click="clearAllFilters()"
          class="link"
          >Reset&nbsp;Filters</a
        >
        <ExportDropdown
          :exportType="exportFormats"
          @export="exportCards"
          :tooltip="exportTooltip"
          :disabled="columnsPerPage > 10" />
      </div>
      <template #filter-bar-right>
        <div class="mr-4 ml-auto flex items-center gap-2">
          <toggle
            id="toggle-show-non-recurring-policies"
            label="Include Non-Recurring Policies"
            v-model="showNonRecurringPolicies"
            @update:model-value="viewOptionsUpdated" />
          <toggle
            v-model="showExternal"
            @update:model-value="viewOptionsUpdated"
            :label="includeExternalPoliciesLabel()"
            id="showExternal"
            class="mr-3" />

          <div class="mr-2 flex items-center justify-end">
            <button
              class="flex h-8 items-center justify-center rounded-l-md rounded-r-none border border-r-0 border-solid border-gray-200 bg-white px-1.5 text-xs font-bold hover:bg-gray-100 disabled:bg-gray-400 disabled:opacity-40"
              @click="removeColumn"
              :disabled="columnsPerPage === defaultColumns">
              <Minus :size="16" />
            </button>
            <Tooltip
              text="Reset Columns"
              position="top">
              <button
                class="flex h-8 w-8 items-center justify-center rounded-none border border-t border-b border-solid border-gray-200 bg-white text-xs font-bold hover:bg-gray-100 disabled:opacity-40"
                @click="resetZoom">
                {{ columnsPerPage }}
              </button>
            </Tooltip>
            <button
              class="mr-0.5 flex h-8 items-center justify-center rounded-l-none rounded-r-md border border-l-0 border-solid border-gray-200 bg-white px-1.5 text-xs font-bold hover:bg-gray-100 disabled:bg-gray-400 disabled:opacity-40"
              @click="addColumn"
              :disabled="columnsPerPage === maxColumns">
              <Plus :size="16" />
            </button>
            <Tooltip
              text="Show All"
              position="top">
              <AppButton
                class="ml-1.5"
                :disabled="columnsPerPage === maxColumns"
                @click="showAllColumns">
                <LucideSheet :size="18" />
              </AppButton>
            </Tooltip>
          </div>
        </div>
      </template>
    </FilterBar>
    <PageSpinner v-if="loading" />
    <div
      v-else
      class="flex max-w-full flex-row justify-between"
      ref="cardViewRef">
      <div
        class="bttn flex h-full max-w-10 min-w-10 cursor-pointer items-center justify-center opacity-20"
        :class="{ disabled: !hasPrevPage }"
        @click="prevPage"
        data-test="btn-prev">
        <ChevronLeft />
      </div>

      <div class="card-view-container h-auto overflow-y-scroll">
        <div
          id="card-view"
          class="matrix"
          :style="gridStyle">
          <div
            class="header__body sticky top-0 z-50 grid h-full grid-cols-6 gap-0.5 rounded-sm bg-white text-center uppercase"
            v-for="account of pageOfAccounts"
            :key="account.accountID">
            <div class="col-span-6 row-start-1 -mb-0.5 overflow-hidden bg-white px-2 text-nowrap normal-case">
              <router-link
                :to="`/account/${account.accountID}/overview`"
                class="url-text account-name-link">
                <div
                  class="overflow-hidden font-bold text-ellipsis"
                  :title="account.accountName">
                  {{ account.accountName }}
                </div>
              </router-link>
            </div>

            <div class="header__item col-span-3 col-start-1 row-start-2 flex flex-col items-center justify-center">
              <div class="font-bold">{{ filters.compactNumber(account.totalPolicies) || '' }}</div>
              <div class="text-[8pt]">Policies</div>
            </div>

            <div class="header__item col-span-3 col-start-4 row-start-2 flex flex-col items-center justify-center">
              <div class="font-bold">{{ filters.compactMoney(account.totalPremium) || '' }}</div>
              <div class="text-[8pt]">Total Premium</div>
            </div>

            <div class="header__item col-span-2 col-start-1 row-start-3 flex flex-col items-center justify-center">
              <div class="font-bold">{{ filters.compactMoney(account.exposureAnnualRevenue) || '' }}</div>
              <div class="text-[8pt]">Annual Revenue</div>
              <div class="text-[7pt] opacity-85">
                {{ filters.date(account.exposureAnnualRevenueEffectiveDate) || '' }}
              </div>
            </div>

            <div class="header__item col-span-2 col-start-3 row-start-3 flex flex-col items-center justify-center">
              <div class="font-bold">{{ filters.compactNumber(account.exposureEmployeeCount) || '' }}</div>
              <div class="text-[8pt]">Employees</div>
              <div class="text-[8pt] opacity-85">{{ filters.date(account.exposureEmployeeCountEffectiveDate) || '' }}</div>
            </div>

            <div class="header__item col-span-2 col-start-5 row-start-3 flex flex-col items-center justify-center">
              <div class="font-bold">{{ filters.compactMoney(account.exposurePayroll) || '' }}</div>
              <div class="text-[8pt]">Payroll</div>
              <div class="text-[8pt] opacity-85">
                {{ filters.date(account.exposurePayrollEffectiveDate) || '' }}
              </div>
            </div>
          </div>
          <template v-for="coverageType in coverageTypes">
            <div
              class="card flex justify-center"
              :class="{
                empty: cardEmpty(account.accountID, coverageType),
                external: isExternal(account.accountID, coverageType),
              }"
              v-for="account of pageOfAccounts"
              :key="`${coverageType}-${account.accountID}`">
              <div v-if="cardEmpty(account.accountID, coverageType)">
                <div class="card-body empty h-full">
                  <div class="coverage">{{ coverageType }}</div>
                </div>
              </div>
              <div
                v-else
                class="flex h-full w-full flex-col">
                <div
                  v-for="(card, index) in getCards(account.accountID, coverageType)"
                  :key="index"
                  class="flex h-full flex-col">
                  <!-- Card Header -->
                  <div class="card-header">
                    <div class="mt-1 mr-1 mb-2 ml-2.5 flex">
                      <span class="w-full text-sm font-bold">
                        <span class="mr-1.5 inline align-text-top">{{ `${coverageType}${card.isChildPolicy ? ' (Packaged)' : ''}` }}</span>

                        <div
                          v-if="hasChip(account.accountID, coverageType)"
                          class="mr-1 inline-flex h-5 w-fit rounded-xl px-2 align-baseline text-[8pt] font-bold"
                          :class="policyChipColorFor(account.accountID, coverageType)">
                          <div class="self-center">{{ headerChipLabelFor(account.accountID, coverageType) }}</div>
                        </div>
                      </span>
                    </div>
                  </div>

                  <!-- Card Body -->
                  <div class="card-body h-full">
                    <ul>
                      <li class="expiration-date">
                        <span>Expires </span>
                        <b>{{ filters.date(card.lineExpirationDate) }}</b>
                      </li>
                      <li class="insurer-group">
                        <span>Issued by </span>
                        <b>{{ card.primaryInsurerGroup }}</b>
                      </li>
                      <li v-if="card.isChildPolicy">
                        <span>Package in </span>
                        <b>{{ getParentCoverageType(card) }}</b>
                      </li>
                      <li
                        class="agency-name"
                        v-if="isExternal(account.accountID, coverageType) && !isUserExternalClient() && card.agencyName">
                        <span>Placed by </span>
                        <b>{{ card.agencyName }}</b>
                      </li>
                      <li
                        class="agency-name"
                        v-if="card.packagedCoverageTypes">
                        <span>Packaged with </span>
                        <b>{{ card.packagedCoverageTypes }}</b>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Card Stats -->
              <div
                class="card-stats"
                v-if="!cardEmpty(account.accountID, coverageType)">
                <div class="stat">
                  <dfn>Limit</dfn>
                  <h2>{{ filters.compactMoney(totalLimitFor(account.accountID, coverageType) || 0) }}</h2>
                </div>
                <div class="stat">
                  <dfn>Retention</dfn>
                  <h2>{{ filters.compactMoney(totalFor(account.accountID, coverageType, 'retention') || 0) }}</h2>
                </div>
                <div class="stat">
                  <dfn>Premium</dfn>
                  <h2>{{ filters.compactMoney(totalFor(account.accountID, coverageType, 'policyPremium') || 0) }}</h2>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div
        class="bttn flex h-full max-w-10 min-w-10 cursor-pointer items-center justify-center opacity-20"
        :class="{ disabled: !hasNextPage }"
        @click="nextPage"
        data-test="btn-next">
        <ChevronRight />
      </div>
    </div>
  </div>
</template>

<script>
import FilterBar from '@/components/v2/FilterBar.vue';
import GenericMultiFilter from '@/components/v2/GenericMultiFilter.vue';
import PageSpinner from '@/components/v2/PageSpinner.vue';
import { accountService, parentAccountService, policySourceService } from '@/services/api';
import { chipColors, colorFromAbbrev } from '@/utilities/Color';
import { PolicySourceControlling, PolicySourceParticipant } from '@/utilities/PolicySources';
import { AccountStatusProspect } from '@/utilities/Account';
import { IsNullOrUndefinedOrNaN } from '@/utilities/Helpers';
import { includeExternalPoliciesLabel } from '@/utilities/OrgConfig';
import { filters } from '@/utilities/Filters';
import AppButton from '@/components/v2/AppButton.vue';
import Toggle from '@/components/v2/Toggle.vue';
import Tooltip from '@/components/v2/Tooltip.vue';
import store, { isUserExternalClient } from '@/store';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import ExportDropdown from '@/components/ExportDropdown.vue';
import { ChevronRight, ChevronLeft, Plus, Minus, LucideSheet } from '@lucide/vue';

export default {
  name: 'ParentAccountCardView',
  components: {
    AppButton,
    ExportDropdown,
    FilterBar,
    Toggle,
    Tooltip,
    PageSpinner,
    GenericMultiFilter,
    ChevronLeft,
    ChevronRight,
    Plus,
    Minus,
    LucideSheet,
  },
  data: () => ({
    filters,
    loading: true,
    allCards: undefined,
    cards: undefined,
    allCoverageTypes: undefined,
    accounts: undefined,
    pageNumber: 0,
    columnsPerPage: 6,
    defaultColumns: 6,
    isZooming: false,
    selectedViewOptions: [],
    requiresRefresh: new Set(['excludeRunoff', 'includeExternalPolicies']),
    showExternal: true,
    showNonRecurringPolicies: false,
    accountStatus: '',
    accountName: '',
    companies: [], // array of { label: accountName, value: accountID}
    companiesMap: new Map(), // key: accountID, value: accountName
    selectedCompanies: [], // array of account ids
    coverageTypeList: [],
    selectedCoverageTypeIndexes: [],
    exportFormats: ['PDF', 'PNG'],
    policySources: [],
  }),
  computed: {
    filteredCompanies() {
      let companies = this.companies;

      if (this.selectedCoverageTypes.length > 0) {
        companies = companies.filter((company) => this.hasCoverageTypes(company.value));
      }

      return companies;
    },
    cardViewHeight() {
      const registry = store.state.resizerRegistry;
      return window.innerHeight - (registry['filter-bar']?.height ?? 0) - (registry['header']?.height ?? 0) - 220;
    },
    selectedCoverageTypes: {
      get() {
        return this.selectedCoverageTypeIndexes.map((i) => this.coverageTypeList[i]?.value);
      },
      set(newValues) {
        this.selectedCoverageTypeIndexes = newValues.map((c) => this.coverageTypeValues.indexOf(c));
      },
    },
    gridStyle() {
      return { gridTemplateColumns: `repeat(${this.pageOfAccounts.length}, 1fr` };
    },
    pageOfAccounts() {
      const start = this.pageNumber * this.columnsPerPage;
      const end = start + this.columnsPerPage;
      //if selected companies filter
      if (this.selectedCompanies.length > 0) {
        return this.accounts?.filter((account) => this.selectedCompanies.includes(parseInt(account.accountID))).slice(start, end);
      }

      // if there are selected coverage types, filter accounts based on that
      if (this.selectedCoverageTypes.length > 0) {
        return this.accounts?.filter((account) => this.hasCoverageTypes(account.accountID)).slice(start, end);
      } else {
        return this.accounts?.slice(start, end);
      }
    },
    coverageTypes() {
      let coverages = this.allCoverageTypes;
      if (this.selectedCoverageTypes.length > 0) {
        coverages = this.allCoverageTypes.filter((coverageType) => this.selectedCoverageTypes.includes(coverageType));
      }

      // filter coverage types based on selected companies
      if (this.selectedCompanies.length > 0) {
        coverages = coverages.filter((coverageType) =>
          this.cards.some((card) => this.selectedCompanies.includes(parseInt(card.accountID)) && card.coverageType === coverageType),
        );
      }

      // filter a coverage type if all cards are empty in that row
      coverages = coverages.filter((coverageType) => {
        if (this.selectedCompanies.length > 0) {
          return !this.selectedCompanies.every((accountId) =>
            this.cardEmpty(this.accounts.find((account) => parseInt(account.accountID) === accountId)?.accountID, coverageType),
          );
        }

        return !this.accounts.every((account) => {
          return this.cardEmpty(account.accountID, coverageType);
        });
      });

      // want to display packaged policies on top, so if at least one card is packaged, bring up the entire row
      coverages.sort((a, b) => {
        if (this.cards.filter((card) => card.coverageType === a).some((card) => card.packagedCoverageTypes)) {
          return -1;
        }
        if (this.cards.filter((card) => card.coverageType === b).some((card) => card.packagedCoverageTypes)) {
          return 1;
        }
        return a.localeCompare(b);
      });

      return coverages;
    },
    hasNextPage() {
      if (this.selectedCompanies.length > 0) {
        return this.selectedCompanies.length > this.pageNumber * this.columnsPerPage + this.columnsPerPage;
      }
      return this.filteredCompanies.length > this.pageNumber * this.columnsPerPage + this.columnsPerPage;
    },
    hasPrevPage() {
      return this.pageNumber > 0;
    },
    maxColumns() {
      if (this.selectedCompanies.length > 0) {
        return this.selectedCompanies.length;
      } else {
        return this.filteredCompanies.length;
      }
    },
    numPages() {
      if (this.selectedCompanies.length > 0) {
        return Math.ceil(this.selectedCompanies.length / this.columnsPerPage);
      }
      return Math.ceil(this.filteredCompanies.length / this.columnsPerPage);
    },
    viewOptions: {
      get() {
        return this.selectedViewOptions;
      },
      set(newValue) {
        const newValues = new Set(newValue.filter((v) => this.requiresRefresh.has(v)));
        const oldValues = new Set(this.selectedViewOptions.filter((v) => this.requiresRefresh.has(v)));

        this.dirty = newValues.size !== oldValues.size;
        this.selectedViewOptions = newValue;
      },
    },
    exportTooltip() {
      // max limit on exported columns otherwise the export will fail
      if (this.columnsPerPage > 10) {
        return 'Export is limited to 10 companies';
      }
      return '';
    },
    resolvedShowExternal() {
      return this.getShowExternalQueryParam() ?? (this.accountStatus === AccountStatusProspect ? true : this.showExternal);
    },
    coverageTypeValues() {
      return this.coverageTypeList.map((c) => c.value);
    },
    filterApplied() {
      return this.selectedCompanies.length > 0 || this.selectedCoverageTypes.length > 0;
    },
  },
  watch: {
    selectedCompanies() {
      this.resetZoom();
      this.setQueryParamsFromFilters();
    },
    selectedCoverageTypeIndexes() {
      this.setQueryParamsFromFilters();
    },
    columnsPerPage() {
      this.setQueryParamsFromFilters();
    },
  },
  methods: {
    includeExternalPoliciesLabel,
    isUserExternalClient,
    nextPage() {
      if (this.hasNextPage) {
        this.pageNumber++;
      }
    },
    prevPage() {
      if (this.hasPrevPage) {
        this.pageNumber--;
      }
    },
    addColumn() {
      if (this.columnsPerPage === this.maxColumns) {
        return;
      }

      this.isZooming = true;
      this.columnsPerPage++;

      if (this.pageNumber >= this.numPages) {
        this.prevPage();
      }
    },
    removeColumn() {
      if (this.columnsPerPage === this.defaultColumns) {
        return;
      }

      this.isZooming = true;
      if (this.columnsPerPage > 0) {
        this.columnsPerPage--;
      }
    },
    showAllColumns() {
      this.isZooming = true;
      this.columnsPerPage = this.maxColumns;
      this.pageNumber = 0;
    },
    resetZoom() {
      this.isZooming = false;
      this.columnsPerPage = Math.min(this.maxColumns, this.defaultColumns);
      this.pageNumber = 0;
    },
    cardEmpty(accountID, coverageType) {
      const card = this.cardFor(accountID, coverageType);
      return !card?.policyID;
    },
    isExternal(accountID, coverageType) {
      const policySourceID = this.cardFor(accountID, coverageType).policySourceID;
      if (policySourceID === undefined || policySourceID === null) {
        return false;
      }
      return this.policySources
        .filter((ps) => ps.IsExternal)
        .map((ps) => ps.ID)
        .includes(policySourceID);
    },
    agencyColor(accountID, coverageType) {
      return colorFromAbbrev(this.cardFor(accountID, coverageType).agencyName);
    },
    cardFor(accountID, coverageType) {
      return (
        [...this.cards]
          .sort((a, b) => b.policySourceID - a.policySourceID)
          .find((c) => c.accountID === accountID && c.coverageType === coverageType) || {}
      );
    },
    getCards(accountID, coverageType) {
      const cards =
        [...this.cards]
          .sort((a, b) => b.policySourceID - a.policySourceID)
          .filter((c) => c.accountID === accountID && c.coverageType === coverageType) || [];

      const childlessPolicy = cards.sort((a, b) => b.policySourceID - a.policySourceID).find((c) => !c.isChildPolicy);
      const childPolicies = cards.filter((c) => c.isChildPolicy);

      if (childlessPolicy) {
        return [childlessPolicy, ...childPolicies];
      } else {
        return childPolicies;
      }
    },
    totalFor(accountID, coverageType, property) {
      return (
        this.cards
          .filter((c) => c.accountID === accountID && c.coverageType === coverageType)
          .map((c) => c[property])
          .filter((x) => !IsNullOrUndefinedOrNaN(x))
          .reduce((acc, curr) => acc + curr, 0) || 0
      );
    },
    totalLimitFor(accountID, coverageType) {
      return (
        this.cards
          .filter((c) => c.accountID === accountID && c.coverageType === coverageType)
          .map((c) => c[c.isChildPolicy ? 'childAggregateLimit' : 'aggregateLimit'])
          .filter((x) => !IsNullOrUndefinedOrNaN(x))
          .reduce((acc, curr) => acc + curr, 0) || 0
      );
    },
    policyChipColorFor(accountID, coverageType) {
      const policySourceID = this.cardFor(accountID, coverageType).policySourceID;

      switch (policySourceID) {
        case PolicySourceControlling:
          return `${chipColors[1].bgColor} ${chipColors[1].textColor}`;
        case PolicySourceParticipant:
          return `${chipColors[2].bgColor} ${chipColors[2].textColor}`;
        default:
          break;
      }

      return `${chipColors[0].bgColor} ${chipColors[0].textColor}`;
    },
    hasChip(accountID, coverageType) {
      const policySourceID = this.cardFor(accountID, coverageType).policySourceID;

      return [PolicySourceParticipant, PolicySourceControlling].includes(policySourceID);
    },
    headerChipLabelFor(accountID, coverageType) {
      const policySourceID = this.cardFor(accountID, coverageType).policySourceID;

      switch (policySourceID) {
        case PolicySourceControlling:
          return 'Controlling';
        case PolicySourceParticipant:
          return 'Participant';
        default:
          break;
      }

      return '';
    },
    async refresh() {
      this.loading = true;
      this.setFiltersFromQueryParams();

      const account = await accountService.findById(this.$route.params.id);
      this.policySources = await policySourceService.list();
      this.accountStatus = account.accountStatus;
      this.accountName = account.accountName;
      const cards =
        (await parentAccountService.cardsById(this.$route.params.id, {
          showExternal: this.resolvedShowExternal,
          showNonRecurring: this.showNonRecurringPolicies,
        })) || [];
      const coverageTypes = Array.from(cards.reduce((set, card) => set.add(card.coverageType), new Set())).sort();
      const uniqueAccounts = Array.from(
        cards
          .reduce(
            (acc, card) =>
              acc.set(card.accountID, {
                accountID: card.accountID,
                accountName: card.accountName,
                exposureAnnualRevenue: card.exposureAnnualRevenue,
                exposureAnnualRevenueEffectiveDate: card.exposureAnnualRevenueEffectiveDate,
                exposureEmployeeCount: card.exposureEmployeeCount,
                exposureEmployeeCountEffectiveDate: card.exposureEmployeeCountEffectiveDate,
                exposurePayroll: card.exposurePayroll,
                exposurePayrollEffectiveDate: card.exposurePayrollEffectiveDate,
                totalPremium: cards.filter((c) => c.accountID === card.accountID && !c.isChildPolicy).reduce((acc, c) => acc + c.policyPremium, 0),
                totalPolicies: card.totalPolicies,
              }),
            new Map(),
          )
          .values(),
      );
      this.accounts = uniqueAccounts
        .filter(({ accountID }) => '' + accountID === '' + this.$route.params.id)
        .concat(
          uniqueAccounts
            .filter(({ accountID }) => '' + accountID !== '' + this.$route.params.id)
            .sort((a, b) => a.accountName.localeCompare(b.accountName)),
        );
      this.allCards = cards;
      this.cards = cards;
      this.allCoverageTypes = coverageTypes;
      this.coverageTypeList = coverageTypes.map((c) => ({ label: c, value: c }));
      this.companiesMap = cards.reduce((companies, card) => companies.set(parseInt(card.accountID), card.accountName), new Map());
      this.companies = Array.from(this.companiesMap, ([value, label]) => ({ label, value })).sort((a, b) => a.label.localeCompare(b.label));

      this.loading = false;
    },
    handleResize() {
      this.setCardViewHeight();

      if (this.isZooming) {
        return;
      }

      if (window.matchMedia('(max-width: 600px)').matches) {
        this.defaultColumns = 1;
      } else if (window.matchMedia('(max-width: 800px)').matches) {
        this.defaultColumns = 2;
      } else if (window.matchMedia('(max-width: 1000px)').matches) {
        this.defaultColumns = 3;
      } else if (window.matchMedia('(max-width: 1200px)').matches) {
        this.defaultColumns = 4;
      } else if (window.matchMedia('(max-width: 1500px)').matches) {
        this.defaultColumns = 5;
      } else {
        this.defaultColumns = 6;
      }

      this.columnsPerPage = this.defaultColumns;
    },
    async viewOptionsUpdated() {
      this.$emit('change-external', this.showExternal);
      // refresh() re-reads the filters back out of the URL, so the new values have to land there first,
      // and this has to be the only writer of ext/nr — a competing replace() cancels this navigation
      await this.setQueryParamsFromFilters();
      await this.refresh();
    },
    clearFilter() {
      this.showExternal = false;
    },
    async exportCards(type, callback) {
      const cardViewElement = document.querySelector('#card-view');
      if (cardViewElement) {
        let uri;
        try {
          uri = await toPng(cardViewElement, { backgroundColor: 'white', skipFonts: true });
        } catch (error) {
          console.error('Failed to convert card view to PNG', error);
          return;
        }

        if (type === 'PDF') {
          const image = await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
              resolve(img);
            };
            img.onerror = (error) => {
              console.error(`Failed to load image: ${uri}`, error);
              reject(error);
            };
            img.src = uri;
          });

          const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'pt',
            format: 'a4',
          });

          const pdfWidth = pdf.internal.pageSize.getWidth();

          const scale = Math.min(1, pdfWidth / image.width);
          const scaledWidth = image.width * scale;
          const scaledHeight = image.height * scale;

          try {
            pdf.addImage(image.src, 'PNG', 0, 0, scaledWidth, scaledHeight);
            pdf.save(`${this.accountName?.replace(' ', '-')}-Card-View.pdf`);
          } catch (error) {
            console.error('Failed to save PDF', error);
            return;
          }
        } else if (type === 'PNG') {
          const link = document.createElement('a');
          link.href = uri;
          link.download = `${this.accountName?.replace(' ', '-')}-Card-View.png`;
          link.click();
        }
      } else {
        console.error('Card View element not found');
      }

      callback();
    },
    getParentCoverageType(card) {
      return this.allCards.find((c) => c.policyID === card.policyID && !c.isChildPolicy)?.coverageType;
    },
    async setQueryParamsFromFilters() {
      const query = {};
      if (this.selectedCompanies.length > 0) {
        query.co = this.selectedCompanies.join(',');
      }

      if (this.selectedCoverageTypes.length > 0) {
        query.cov = this.selectedCoverageTypes.map((c) => this.coverageTypeValues.indexOf(c)).join(',');
      }

      if (this.showExternal) {
        query.ext = 'true';
      } else {
        query.ext = 'false';
      }

      if (this.showNonRecurringPolicies) {
        query.nr = 'true';
      } else {
        query.nr = 'false';
      }

      query.col = this.columnsPerPage.toString();

      if (Object.keys(query).length) {
        if (JSON.stringify(this.$route.query) !== JSON.stringify(query)) {
          await this.$router.replace({ query }).catch(() => {
            return '';
          });
        }
      }
    },
    setFiltersFromQueryParams() {
      const { co, cov, ext, nr, col } = this.$route.query;

      if (co && typeof co === 'string') {
        this.selectedCompanies = co.split(',').map((c) => parseInt(c));
      }

      if (cov && typeof cov === 'string') {
        this.selectedCoverageTypeIndexes = cov.split(',').map((c) => parseInt(c));
      }

      this.showExternal = ext !== undefined ? ext === 'true' : this.showExternal;

      this.showNonRecurringPolicies = nr !== undefined ? nr === 'true' : this.showNonRecurringPolicies;

      if (col && typeof col === 'string') {
        this.columnsPerPage = parseInt(col);
      }
    },
    getShowExternalQueryParam() {
      const { ext } = this.$route.query;
      return ext !== undefined ? ext === 'true' : undefined;
    },
    setCardViewHeight() {
      if (this.$refs.cardViewRef) {
        this.$refs.cardViewRef.style.height = this.cardViewHeight && `${this.cardViewHeight}px`;
      }
    },
    hasCoverageTypes(accountId) {
      const accountCoverageTypes = this.allCards.filter((card) => card.accountID === accountId).map((card) => card.coverageType);
      return this.selectedCoverageTypes.some((coverageType) => accountCoverageTypes.includes(coverageType));
    },
    clearAllFilters() {
      this.selectedCompanies = [];
      this.selectedCoverageTypeIndexes = [];
      this.setQueryParamsFromFilters();
    },
  },
  async created() {
    this.handleResize();
    await this.refresh();
    await this.setQueryParamsFromFilters();
  },
  mounted() {
    window.addEventListener('resize', this.handleResize, { passive: true });
    this.setCardViewHeight();
  },
  unmounted() {
    window.removeEventListener('resize', this.handleResize);
  },
  updated() {
    this.setCardViewHeight();
  },
};
</script>

<style scoped>
.bttn:hover {
  background: #f5f5f5; /* var(--v-offwhite-base) */
  opacity: 1 !important;
}

.bttn:active {
  background: #e7e7e7; /* var(--v-border-base) */
}

.bttn.disabled {
  visibility: hidden;
}

.matrix {
  display: grid;
  grid-gap: 0.5rem;
}

.card {
  border: 1px solid #afafaf; /* var(--v-border-darken2) */
  border-radius: 4px;
  background: #fff;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr fit-content(20%);
  grid-template-rows: auto 1fr;
  grid-template-areas: 'body stats';
}

.header__item {
  padding: 0.5rem;
  border: 1px solid #afafaf;
  border-radius: 4px;
  background-color: white;
}

.header__body {
  grid-template-rows: auto repeat(2, minmax(0, 1fr));
}

.card.empty {
  align-content: center;
  align-items: center;
  min-height: 4rem;
  grid-template-rows: 1fr;
  grid-template-areas: 'body';
  background: repeating-linear-gradient(-45deg, white, #e0e0e0 5px);
}

.card.external {
  border: 2px solid #e1251b; /* var(--v-anchor-base) */
}

.card.empty .card-header,
.card.empty .card-stats {
  display: none;
}

.card.empty .card-body.empty {
  text-align: center;
}

.card.empty .card-body.empty .coverage {
  font-weight: 600;
  opacity: 0.5;
}

.card .card-body {
  padding: 0.5rem;
}

.card .card-header {
  grid-area: header;
  background-color: #4d9cb9;
  color: white;
}

.card:not(.empty) .card-body {
  grid-area: body;
  background-color: #415363;
  color: white;
}

.card .card-stats {
  grid-area: stats;
}

.card .card-header {
  border-bottom: 1px solid #afafaf; /* var(--v-border-darken2) */
}

.card:not(.empty) .card-body {
  font-size: 0.9em;
  padding-top: 0.75rem;
}

.card .card-body ul {
  padding: 0 1rem;
  list-style: none;
}

.card .card-body li {
  padding: 0.1rem 0;
}

.card .card-body li:before {
  content: '\2022';
  display: inline-block;
  width: 1rem;
  margin-left: -1rem;
  font-weight: bolder;
}

.card .coverage {
  font-weight: bold;
  font-size: 0.9rem;
}

.card:not(.header):hover {
  box-shadow: 2px 2px 2px #e7e7e7; /* var(--v-border-base) */
}

.card .card-stats {
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  align-content: center;
  grid-row-gap: 1rem;
  background: #f5f5f5; /* var(--v-offwhite-base) */
  border-left: 1px solid #afafaf; /* var(--v-border-darken2) */
  border-radius: 0 4px 4px 0;
  padding: 0.5rem 0.5rem 1rem;
}

.stat {
  text-align: center;
}

.stat h2 {
  font-size: 1.1em;
  line-height: 1;
}

.stat dfn {
  text-transform: uppercase;
  font-style: normal;
  font-size: 0.8em;
  color: #717171; /* var(--v-gray-base) */
}

.menu {
  padding: 1rem 0 0 0;
  min-width: 300px;
}

.menu > h4 {
  padding: 0 1rem;
}

.menu > .fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 1rem;
  padding: 0 1rem 1rem;
}

.menu > .fields > .field > h5 {
  font-size: 0.8rem;
  letter-spacing: 0.02em;
  margin: 1rem 0 0.5rem;
  text-transform: uppercase;
  font-weight: 500;
}

.menu > .fields > .field > label {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-column-gap: 0.5rem;
  align-items: center;
  align-content: center;
  padding: 0.2rem 0;
  cursor: pointer;
  user-select: none;
}

.menu > .buttons {
  padding: 1rem;
  border-top: 1px solid #e7e7e7; /* var(--v-border-base) */
  background: #f8f8f8; /* var(--v-gray-lighten5) */
  display: flex;
  justify-content: space-between;
}

.menu > .buttons.one {
  justify-content: flex-end;
}
</style>
